"use client";

import { useEffect, useState } from "react";

const JOURNEY_ID_STORAGE_KEY = "statsig_journey_id";

function generateJourneyId(): string {
  // crypto.randomUUID is available in modern browsers and React Native (Hermes
  // ≥0.74). Fallback to a Math.random-based UUIDv4 for the rare environments
  // that lack it (older mobile webviews); collisions are not load-bearing here
  // because journey_id is scoped to a single session per externalAccountId.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Mints a stable journey_id at first paywall view and persists it in
 * sessionStorage so each conversion attempt is its own analytics unit.
 *
 * Carry the returned id through:
 *   1. Paywall events (`paywall_viewed`, `cta_clicked`, etc.)
 *   2. Checkout components
 *   3. Stripe `subscription.metadata.journey_id` at SetupIntent /
 *      Subscription creation, so it survives into webhook events
 *
 * Behavior:
 *   - SSR: returns `null` until the client hydrates.
 *   - First call per session: mints + stores a UUID and returns it.
 *   - Subsequent calls in the same session: returns the stored UUID.
 *   - sessionStorage is cleared when the tab closes — exactly the right
 *     scope for "one paywall attempt".
 *
 * See `packages/statsig/TAXONOMY.md` → Identity & attribution.
 */
export function useJourneyId(): string | null {
  const [journeyId, setJourneyId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let stored: string | null = null;
    try {
      stored = window.sessionStorage.getItem(JOURNEY_ID_STORAGE_KEY);
    } catch {
      // sessionStorage may be unavailable (private mode, restricted webview).
      // Fall through to mint a fresh id; persistence is best-effort.
    }
    if (stored) {
      setJourneyId(stored);
      return;
    }
    const fresh = generateJourneyId();
    try {
      window.sessionStorage.setItem(JOURNEY_ID_STORAGE_KEY, fresh);
    } catch {
      // ignore — see above
    }
    setJourneyId(fresh);
  }, []);

  return journeyId;
}

/**
 * Read the current journey_id without subscribing. Use in event handlers
 * where you don't want a hook re-render. Returns `null` on the server or
 * before the first paywall view.
 */
export function readJourneyId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(JOURNEY_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the journey_id. Called when a conversion attempt resolves
 * (success or hard abandon) so the next paywall view starts a new journey.
 * Optional — sessionStorage cleanup happens naturally on tab close.
 */
export function clearJourneyId(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(JOURNEY_ID_STORAGE_KEY);
  } catch {
    // ignore
  }
}
